import { Bell, CheckCircle2, Info, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { onToast } from "../lib/toast";
import { onLiveNotification, useLiveNotifications } from "../lib/ws";

interface Toast {
  key: number;
  title: string;
  body?: string;
  kind: "notification" | "success" | "error" | "info";
}

/**
 * Opens the live WebSocket and renders transient toasts for both incoming
 * notifications and manual `toast.*` calls. Mounted inside the app shell.
 */
export function Toaster(): JSX.Element {
  useLiveNotifications();
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    let counter = 0;
    const add = (t: Omit<Toast, "key">): void => {
      const key = ++counter;
      setToasts((cur) => [...cur, { ...t, key }]);
      setTimeout(() => setToasts((cur) => cur.filter((x) => x.key !== key)), 6000);
    };
    const offNote = onLiveNotification((n) => add({ title: n.title, body: n.body, kind: "notification" }));
    const offToast = onToast((m) => add({ title: m.text, kind: m.kind }));
    return () => {
      offNote();
      offToast();
    };
  }, []);

  const icon = (kind: Toast["kind"]): JSX.Element => {
    if (kind === "success") return <CheckCircle2 size={15} />;
    if (kind === "error") return <XCircle size={15} />;
    if (kind === "info") return <Info size={15} />;
    return <Bell size={15} />;
  };
  const tone = (kind: Toast["kind"]): string =>
    kind === "error"
      ? "bg-red-500/15 text-red-600"
      : kind === "success"
        ? "bg-green-500/15 text-green-600"
        : "bg-accent-500/15 text-accent-600";

  return (
    <div className="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4">
      {toasts.map((t) => (
        <div
          key={t.key}
          className="pointer-events-auto flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
        >
          <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone(t.kind)}`}>
            {icon(t.kind)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">{t.title}</p>
            {t.body && <p className="text-xs text-slate-500">{t.body}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
